import { Alpine } from '../core/alpine-core.js';
import { refreshIcons } from '../core/icons.js';
import { apiFetch } from './api.js';
import { GridStack } from './scorer-layout-gridstack-host.js';
import { mountBuilderPaletteDrag } from './scorer-layout-builder-palette-drag.js';
import { initPressRipple } from '../core/press-ripple.js';
import {
  scorerLayoutCatalogState,
  createScorerLayoutInlinePreviewMethods,
  createPreviewMockMatch,
} from '../shared/scorer-layout-inline-preview.js';
import {
  SCORER_LAYOUT_PROFILES,
  SCORER_LAYOUT_GRID_PRESETS,
  SCORER_LAYOUT_FRAME_PRESETS,
  createEmptyLayout,
  createEmptyProfile,
  validateLayout,
  computeHeightBudget,
  makeWidgetId,
  applyGridPreset,
  normalizeLayoutSlug,
  normalizeLoadedLayout,
  syncLayoutProfilesFromDesign,
  SCORER_LAYOUT_DESIGN_PROFILE,
  mergeLayoutProfileFrame,
  toLayoutThemeId,
  getStarterWidgetTemplates,
} from '../shared/scorer-layout-schema.js';
import {
  GRIDSTACK_WIDGET_META_KEY,
  buildTrackOverlayStyle,
  computeBuilderFrameSize,
  computeBuilderGridMetrics,
  profileToGridStackOptions,
  syncWidgetsFromGrid,
  loadGridFromProfile,
  readPaletteKeyFromNode,
  gridStackToSchemaXY,
  paletteItemToGridStackWidget,
  widgetToGridStackNode,
  findFirstOpenCell,
  gridClientPixelToDropSlot,
  findNearestDropSlot,
  refreshBuilderGridDrag,
  syncBuilderGridCanvas,
  syncBuilderTrackOverlayCells,
  paintBuilderTrackOverlayCells,
  syncGridWidgetMeta,
  clearBuilderTrackOverlayHighlight,
  paintBuilderTrackOverlayHighlight,
} from '../shared/scorer-layout-gridstack-adapter.js';
import {
  listScorerWidgetsByCategory,
  createWidgetFromCatalog,
  getScorerWidgetDef,
  getScorerWidgetPaletteDef,
  widgetLabel,
  widgetHasButtonChrome,
  SCORER_DIGIT_GRID_TOP_DIGITS,
  widgetHasPlayerSlot,
  widgetHasHeaderDisplayOptions,
  widgetHasScoreboardDisplayOptions,
  widgetHasDigitGridOptions,
  SCORER_PLAYER_SLOT_OPTIONS,
  SCORER_PLAYER_CARD_REMAINING_SIZE_OPTIONS,
  normalizePlayerSlot,
  widgetPlayerSlotLabel,
  widgetShowAvatar,
  widgetShowStats,
  widgetShowLegs,
  widgetShowRemaining,
  widgetShowThrowBadge,
  widgetShowNextThrower,
  widgetShowLastScore,
  widgetShowQuickScores,
  SCORER_DIGIT_GRID_WITH_QUICK_ROW_COUNT,
  widgetPlayerCardRemainingSize,
  normalizePlayerCardRemainingSize,
  widgetHasButtonIconOptions,
  resolveWidgetButtonIconClass,
  resolveWidgetButtonIconId,
  widgetDefaultButtonIconId,
  widgetButtonFaceText,
  widgetConfigDisplayLabel,
  SCORER_WIDGET_BUTTON_ICON_OPTIONS,
  widgetActionLabel,
  widgetDisplayLabel,
  listActionPresetsForWidgetType,
  listActionPresetsGrouped,
  applyActionPresetToWidget,
  resolveWidgetActionId,
  widgetHasSurfaceFill,
  SCORER_WIDGET_SURFACE_FILLS,
  resolveWidgetSurfaceFillId,
  resolveWidgetSurfaceOpacityPercent,
  widgetSurfaceCssVars,
  widgetSurfaceClass,
  widgetSurfaceFillDef,
  widgetSizeLimits,
  widgetCatalogIcon as catalogWidgetIcon,
  widgetCatalogDescription as catalogWidgetDescription,
  widgetCatalogSizeLabel as catalogWidgetSizeLabel,
} from '../shared/scorer-layout-widget-catalog.js';

initPressRipple();

function cloneLayout(row) {
  return normalizeLoadedLayout(JSON.parse(JSON.stringify(row || createEmptyLayout('new-layout'))));
}

async function parseApiJson(res) {
  try {
    return await res.json();
  } catch {
    return {};
  }
}

function formatValidationFeedback(validation) {
  const errors = validation?.errors || [];
  if (!errors.length) return 'Validation failed.';
  const labels = {
    invalid_id: 'Layout id must be a lowercase slug (letters, numbers, hyphens).',
    label_required: 'Layout label is required.',
    no_availability_profile: 'Enable at least one device profile before publishing.',
    tabletLandscape_profile_missing: 'Tablet landscape profile is missing.',
    mobilePortrait_profile_missing: 'Mobile portrait profile is missing.',
    tabletLandscape_height_overflow: 'Tablet layout exceeds the height budget.',
    mobilePortrait_height_overflow: 'Mobile layout exceeds the height budget.',
  };
  return errors
    .map((code) => labels[code] || String(code).replace(/_/g, ' '))
    .join(' · ');
}

function catalogItemForWidget(widget) {
  const type = String(widget?.type || '');
  if (type === 'keypad.digit') {
    return getScorerWidgetPaletteDef(`keypad.digit.${widget?.config?.digit ?? 0}`) || getScorerWidgetDef(type);
  }
  return getScorerWidgetDef(type);
}

function enrichPaletteWidget(catalogItem) {
  return {
    ...catalogItem,
    iconClass: catalogWidgetIcon(catalogItem),
    descriptionText: catalogWidgetDescription(catalogItem),
    sizeLabel: catalogWidgetSizeLabel(catalogItem),
  };
}

function readPaletteKeyFromElement(el) {
  return String(el?.getAttribute?.('data-palette-key') || el?.dataset?.paletteKey || '').trim();
}

function paintPaletteFaIcon(el, classes) {
  if (!el?.parentNode) return;
  const cls = String(classes || '').trim();
  const tag = String(el.tagName || '').toLowerCase();
  if (tag === 'i') {
    el.setAttribute('class', cls);
    refreshIcons(el);
    return;
  }
  const icon = document.createElement('i');
  icon.setAttribute('class', cls);
  icon.setAttribute('aria-hidden', 'true');
  el.replaceWith(icon);
  refreshIcons(icon);
}

function applyPaletteDragMetadata(itemEl, def, enriched = null) {
  if (!itemEl || !def) return null;
  const paletteKey = def.paletteKey || def.type || '';
  itemEl.dataset.paletteKey = paletteKey;
  itemEl.setAttribute('data-palette-key', paletteKey);
  itemEl.setAttribute('gs-w', String(def.defaultColSpan || 1));
  itemEl.setAttribute('gs-h', String(def.defaultRowSpan || 1));
  itemEl.setAttribute('gs-min-w', String(def.minColSpan || 1));
  itemEl.setAttribute('gs-min-h', String(def.minRowSpan || 1));
  const labelEl = itemEl.querySelector('.palette-item-label');
  if (labelEl) labelEl.textContent = enriched?.label || def.label || '';
  const descEl = itemEl.querySelector('.palette-item-desc');
  const hintText = enriched?.descriptionText || catalogWidgetDescription(def) || '';
  if (descEl) descEl.textContent = hintText;
  itemEl.removeAttribute('title');
  itemEl.setAttribute('aria-label', `${enriched?.label || def.label || 'Widget'}. ${hintText}`);
  const hintEl = itemEl.querySelector('.scorer-layout-palette-item__hint');
  if (hintEl) {
    const hintTitle = hintEl.querySelector('.palette-item-label');
    const hintBody = hintEl.querySelector('.palette-item-desc');
    const hintSize = hintEl.querySelector('.palette-item-size');
    const hintIcon = hintEl.querySelector('.scorer-layout-palette-item__hint-icon');
    if (hintTitle) hintTitle.textContent = enriched?.label || def.label || '';
    if (hintBody) hintBody.textContent = hintText;
    if (hintSize) hintSize.textContent = enriched?.sizeLabel || catalogWidgetSizeLabel(def) || '';
    if (hintIcon) {
      paintPaletteFaIcon(
        hintIcon,
        `scorer-layout-palette-item__hint-icon ${catalogWidgetIcon(def)}`,
      );
    }
  }
  const sizeEl = itemEl.querySelector('.palette-item-size');
  if (sizeEl) sizeEl.textContent = enriched?.sizeLabel || catalogWidgetSizeLabel(def) || '';
  const iconEl = itemEl.querySelector('.scorer-layout-palette-item__icon');
  if (iconEl) {
    paintPaletteFaIcon(iconEl, `scorer-layout-palette-item__icon ${catalogWidgetIcon(def)}`);
  }
  return paletteItemToGridStackWidget(def);
}

Alpine.data('scorerLayoutBuilder', () => ({
  ...createScorerLayoutInlinePreviewMethods(),
  layouts: [],
  layout: createEmptyLayout('new-layout'),
  activeProfile: 'tabletLandscape',
  loading: true,
  saving: false,
  publishing: false,
  deleting: false,
  error: '',
  okMsg: '',
  selectedWidgetId: null,
  gridContextMenu: { open: false, x: 0, y: 0, widgetId: '' },
  paletteFilter: '',
  canvasViewportHeight: 480,
  canvasScale: 1,
  collapsedPaletteGroups: {},
  paletteGroupsList: [],
  _canvasResizeHandler: null,
  _gridStack: null,
  _gridSyncPaused: false,
  _paletteDrag: null,
  _gridClickHandler: null,
  _gridContextMenuHandler: null,
  _dismissContextMenuHandler: null,
  _canvasResizeObserver: null,
  _gridInitAttempts: 0,

  profiles: SCORER_LAYOUT_PROFILES,
  framePresets: SCORER_LAYOUT_FRAME_PRESETS,
  surfaceFillOptions: SCORER_WIDGET_SURFACE_FILLS,

  init() {
    this.loadList().then(() => {
      this.$nextTick(() => {
        this.mountCanvasObserver();
        this.reinitGridStack();
      });
    });
    this._canvasResizeHandler = () => {
      const prevScale = this.canvasScale;
      this.measureCanvasViewport();
      if (this.canvasPreviewMode) return;
      if (Math.abs(prevScale - this.canvasScale) > 0.01 || !this._gridStack) {
        this.reinitGridStack();
      }
    };
    globalThis.addEventListener('resize', this._canvasResizeHandler);
    this.$watch('paletteFilter', () => {
      this.rebuildPaletteGroups();
      this.$nextTick(() => {
        this.paintAllPaletteItems();
        this.setupPaletteDrag();
      });
    });
    this.$watch('collapsedPaletteGroups', () => this.setupPaletteDrag());
  },

  destroy() {
    if (this._canvasResizeHandler) {
      globalThis.removeEventListener('resize', this._canvasResizeHandler);
    }
    if (this._canvasResizeObserver) {
      this._canvasResizeObserver.disconnect();
      this._canvasResizeObserver = null;
    }
    this.unbindGridContextMenu();
    this.teardownPaletteDrag();
    this.destroyGridStack();
  },

  handleBuilderEscape() {
    if (this.gridContextMenu?.open) {
      this.closeGridContextMenu();
      return;
    }
    if (this.canvasPreviewMode) this.exitCanvasPreview();
  },

  closeGridContextMenu() {
    if (!this.gridContextMenu?.open) return;
    this.gridContextMenu = { open: false, x: 0, y: 0, widgetId: '' };
  },

  openGridContextMenu(widgetId, clientX, clientY) {
    const id = String(widgetId || '').trim();
    if (!id) return;
    this.selectWidget(id);
    this.gridContextMenu = {
      open: true,
      x: Math.max(8, Number(clientX) || 0),
      y: Math.max(8, Number(clientY) || 0),
      widgetId: id,
    };
  },

  removeContextMenuWidget() {
    this.removeWidgetById(this.gridContextMenu?.widgetId);
  },

  mountCanvasObserver() {
    const el = this.$refs?.canvasViewport;
    if (!el || this._canvasResizeObserver) return;
    this._canvasResizeObserver = new ResizeObserver(() => {
      if (this.loading) return;
      const prevScale = this.canvasScale;
      this.measureCanvasViewport();
      if (this.canvasPreviewMode) return;
      if (!this._gridStack || Math.abs(prevScale - this.canvasScale) > 0.01) {
        this.reinitGridStack();
      }
    });
    this._canvasResizeObserver.observe(el);
  },

  async loadList({ preferId } = {}) {
    this.loading = true;
    this.error = '';
    try {
      const res = await apiFetch('/api/scorer-layouts');
      const data = await parseApiJson(res);
      if (!res.ok) {
        throw new Error(data?.message || data?.error || 'Failed to load layouts');
      }
      this.layouts = Array.isArray(data?.layouts) ? data.layouts : [];
      const targetId = normalizeLayoutSlug(preferId || this.layout?.id);
      if (targetId && this.layouts.some((l) => l.id === targetId)) {
        this.selectLayout(targetId, { skipGrid: true });
      } else if (!targetId && this.layouts.length) {
        this.selectLayout(this.layouts[0].id, { skipGrid: true });
      } else if (!this.layouts.length && !targetId) {
        this.layout = createEmptyLayout('starter-tablet');
      }
    } catch (e) {
      this.error = e?.message || 'Failed to load layouts';
    } finally {
      this.loading = false;
      this.rebuildPaletteGroups();
      this.collapseAllPaletteGroups();
      this.$nextTick(() => {
        this.paintAllPaletteItems();
        this.reinitGridStack();
      });
    }
  },

  selectLayout(id, { skipGrid = false } = {}) {
    const row = this.layouts.find((l) => l.id === id);
    if (!row) return;
    this.layout = cloneLayout(row);
    this.selectedWidgetId = null;
    this.error = '';
    this.okMsg = '';
    if (!skipGrid) {
      this.$nextTick(() => this.reinitGridStack());
    }
  },

  layoutStatusLabel() {
    return this.layout?.published ? 'Published' : 'Draft';
  },

  layoutStatusClass() {
    return this.layout?.published
      ? 'border-emerald-400/30 bg-emerald-500/15 text-emerald-100'
      : 'border-sky-400/30 bg-sky-500/15 text-sky-100';
  },

  isLayoutSelected(id) {
    return normalizeLayoutSlug(this.layout?.id) === normalizeLayoutSlug(id);
  },

  layoutListSelectValue() {
    const id = normalizeLayoutSlug(this.layout?.id || '');
    return this.layouts.some((l) => l.id === id) ? id : '';
  },

  onLayoutSelectChange(event) {
    const id = String(event?.target?.value || '').trim();
    if (id) this.selectLayout(id);
  },

  clearTrackOverlayHighlight() {
    clearBuilderTrackOverlayHighlight(this.$refs?.trackOverlayEl);
  },

  paintTrackOverlaySlot(x, y, w = 1, h = 1) {
    paintBuilderTrackOverlayHighlight(this.$refs?.trackOverlayEl, this._gridStack, x, y, w, h);
  },

  async deleteLayout() {
    const id = this.layoutListSelectValue();
    if (!id) {
      this.error = 'Select a saved layout to delete.';
      return;
    }
    const row = this.layouts.find((l) => l.id === id);
    const label = row?.label || id;
    const ok = globalThis.confirm(
      `Delete layout “${label}” (${id})? This removes the draft and unpublishes it from kiosks.`,
    );
    if (!ok) return;

    this.deleting = true;
    this.error = '';
    this.okMsg = '';
    try {
      const res = await apiFetch(`/api/scorer-layouts/${encodeURIComponent(id)}`, {
        method: 'DELETE',
      });
      const data = await parseApiJson(res);
      if (!res.ok) {
        this.error = data?.message || data?.error || 'Delete failed';
        return;
      }
      const wasCurrent = normalizeLayoutSlug(this.layout?.id) === id;
      await this.loadList();
      if (wasCurrent || !this.layouts.length) {
        this.newLayout();
      } else if (!this.layouts.some((l) => l.id === normalizeLayoutSlug(this.layout?.id))) {
        this.selectLayout(this.layouts[0].id);
      }
      this.okMsg = `Layout “${label}” deleted.`;
    } catch (e) {
      this.error = e?.message || 'Delete failed';
    } finally {
      this.deleting = false;
    }
  },

  newLayout() {
    const slug = normalizeLayoutSlug(`layout-${Date.now().toString(36)}`) || 'new-layout';
    this.layout = createEmptyLayout(slug);
    this.selectedWidgetId = null;
    this.error = '';
    this.okMsg = '';
    this.$nextTick(() => this.reinitGridStack());
  },

  setActiveProfile(profileKey) {
    if (this.activeProfile === profileKey) return;
    if (this.canvasPreviewMode) this.exitCanvasPreview();
    this.syncFromGrid();
    this.activeProfile = profileKey;
    this.selectedWidgetId = null;
    this.$nextTick(() => {
      this.reinitGridStack();
      this.measureCanvasViewport();
    });
  },

  /** Canonical layout data — always the tablet design grid + widgets. */
  designProfileData() {
    if (!this.layout?.profiles) return createEmptyProfile(SCORER_LAYOUT_DESIGN_PROFILE);
    return this.layout.profiles[SCORER_LAYOUT_DESIGN_PROFILE]
      || createEmptyProfile(SCORER_LAYOUT_DESIGN_PROFILE);
  },

  /** Device frame used for builder canvas / inline preview (not a separate layout). */
  previewFrameKey() {
    return this.activeProfile || SCORER_LAYOUT_DESIGN_PROFILE;
  },

  profileDataForCanvas() {
    return mergeLayoutProfileFrame(this.designProfileData(), this.previewFrameKey());
  },

  activeProfileData() {
    return this.designProfileData();
  },

  gridPresetsForProfile() {
    return SCORER_LAYOUT_GRID_PRESETS[SCORER_LAYOUT_DESIGN_PROFILE] || [];
  },

  activePresetId() {
    return this.activeProfileData()?.grid?.presetId || '';
  },

  rebuildPaletteGroups() {
    const q = String(this.paletteFilter || '').trim().toLowerCase();
    this.paletteGroupsList = listScorerWidgetsByCategory(SCORER_LAYOUT_DESIGN_PROFILE).map((g) => ({
      ...g,
      widgets: g.widgets
        .filter((w) => {
          if (!q) return true;
          return `${w.label} ${w.type} ${catalogWidgetDescription(w)}`.toLowerCase().includes(q);
        })
        .map(enrichPaletteWidget),
    })).filter((g) => g.widgets.length || !q);
  },

  paintAllPaletteItems(root = this.$root) {
    const host = root?.querySelector?.('.scorer-layout-palette-host');
    if (!host) return;
    const widgets = [];
    for (const group of this.paletteGroupsList || []) {
      for (const widget of group.widgets || []) widgets.push(widget);
    }
    const itemEls = [...host.querySelectorAll('.scorer-layout-palette-item')];
    itemEls.forEach((el, idx) => {
      const widget = widgets[idx];
      if (!widget) return;
      const enriched = enrichPaletteWidget(widget);
      const def = getScorerWidgetPaletteDef(enriched.paletteKey || enriched.type)
        || getScorerWidgetDef(enriched.type);
      if (def) applyPaletteDragMetadata(el, def, enriched);
    });
  },

  collapseAllPaletteGroups() {
    const next = {};
    const groups = this.paletteGroupsList?.length
      ? this.paletteGroupsList
      : listScorerWidgetsByCategory(SCORER_LAYOUT_DESIGN_PROFILE);
    for (const group of groups || []) {
      next[group.id] = true;
    }
    this.collapsedPaletteGroups = next;
  },

  togglePaletteGroup(groupId) {
    if (this.canvasPreviewMode) return;
    const id = String(groupId || '').trim();
    if (!id) return;
    if (this.isPaletteGroupCollapsed(id)) {
      const next = {};
      for (const group of this.paletteGroupsList || []) {
        if (group.id !== id) next[group.id] = true;
      }
      this.collapsedPaletteGroups = next;
      return;
    }
    this.collapsedPaletteGroups = {
      ...this.collapsedPaletteGroups,
      [id]: true,
    };
  },

  isPaletteGroupCollapsed(groupId) {
    return Boolean(this.collapsedPaletteGroups[groupId]);
  },

  heightBudget() {
    return computeHeightBudget(this.activeProfileData());
  },

  validationPreview() {
    return validateLayout(this.layout, { forPublish: false });
  },

  validationErrors() {
    return this.validationPreview().errors || [];
  },

  validationWarnings() {
    return this.validationPreview().warnings || [];
  },

  validationFeedback() {
    return formatValidationFeedback(this.validationPreview());
  },

  deviceFrameLabel() {
    const frame = SCORER_LAYOUT_FRAME_PRESETS[this.previewFrameKey()]
      || SCORER_LAYOUT_FRAME_PRESETS[SCORER_LAYOUT_DESIGN_PROFILE];
    const profile = this.previewFrameKey() === 'mobilePortrait' ? 'Mobile preview' : 'Tablet preview';
    return `${profile} · ${frame.safeWidth}×${frame.safeHeight}px · one shared grid`;
  },

  trackOverlayStyle() {
    return buildTrackOverlayStyle(
      this.profileDataForCanvas(),
      this.previewFrameKey(),
      this.canvasScale,
    );
  },

  deviceFrameStyle() {
    const metrics = computeBuilderGridMetrics(
      this.profileDataForCanvas(),
      this.previewFrameKey(),
      this.canvasScale,
    );
    return {
      width: `${metrics.width}px`,
      height: `${metrics.height}px`,
      maxWidth: '100%',
    };
  },

  measureCanvasViewport() {
    try {
      const el = this.$refs?.canvasViewport;
      if (!el) return;
      const h = el.clientHeight;
      if (h > 0) this.canvasViewportHeight = Math.max(200, h - 8);
      const prof = this.profileDataForCanvas();
      const frame = prof.frame || SCORER_LAYOUT_FRAME_PRESETS[this.previewFrameKey()];
      const targetW = Number(frame.safeWidth) || 1200;
      const targetH = Number(frame.safeHeight) || 720;
      const maxW = Math.max(280, el.clientWidth - 48);
      const maxH = Math.max(220, Number(this.canvasViewportHeight) || targetH);
      this.canvasScale = Math.min(1, maxH / targetH, maxW / targetW);
      if (this.canvasPreviewMode) {
        this.$nextTick(() => {
          this.syncInlinePreviewDeviceContext?.();
          this.paintInlinePreviewTrackOverlay?.();
        });
      }
    } catch {
      /* ignore */
    }
  },

  destroyGridStack() {
    this.teardownPaletteDrag();
    this.unbindGridContextMenu();
    if (this._gridStack?.el && this._gridClickHandler) {
      this._gridStack.el.removeEventListener('click', this._gridClickHandler);
    }
    this._gridClickHandler = null;
    if (this._gridStack) {
      try {
        this._gridStack.destroy(false);
      } catch {
        /* ignore */
      }
      this._gridStack = null;
    }
    const el = this.$refs?.gridStackEl;
    if (el) {
      el.innerHTML = '';
      delete el.gridstack;
    }
  },

  paintGridOverlay() {
    const el = this.$refs?.trackOverlayEl;
    if (!el) return;
    const metrics = computeBuilderGridMetrics(
      this.profileDataForCanvas(),
      this.previewFrameKey(),
      this.canvasScale,
    );
    paintBuilderTrackOverlayCells(el, metrics);
  },

  reinitGridStack() {
    if (this.loading || this.canvasPreviewMode) return;
    this.measureCanvasViewport();
    this.paintGridOverlay();
    this.destroyGridStack();
    const el = this.$refs?.gridStackEl;
    if (!el) {
      if (this._gridInitAttempts < 8) {
        this._gridInitAttempts += 1;
        this.$nextTick(() => this.reinitGridStack());
      }
      return;
    }
    this._gridInitAttempts = 0;
    const prof = this.profileDataForCanvas();
    const frameKey = this.previewFrameKey();
    const opts = profileToGridStackOptions(prof, frameKey, this.canvasScale);
    this._gridSyncPaused = true;
    this._gridStack = GridStack.init(opts, el);
    if (!this._gridStack) return;
    if (typeof this._gridStack.float === 'function') {
      this._gridStack.float(true);
    }
    syncBuilderGridCanvas(this._gridStack, prof, frameKey, this.canvasScale);
    syncBuilderTrackOverlayCells(
      this.$refs?.trackOverlayEl,
      this._gridStack,
      prof,
      frameKey,
      this.canvasScale,
    );
    this._gridStack.enable();
    this.bindGridEvents(this._gridStack);
    this.bindGridSelectionClick(this._gridStack);
    this.bindGridContextMenu(this._gridStack);
    loadGridFromProfile(this._gridStack, this.designProfileData(), (widget) => this.buildWidgetChipElement(widget));
    refreshBuilderGridDrag(this._gridStack);
    syncBuilderGridCanvas(this._gridStack, prof, frameKey, this.canvasScale);
    syncBuilderTrackOverlayCells(
      this.$refs?.trackOverlayEl,
      this._gridStack,
      prof,
      frameKey,
      this.canvasScale,
    );
    this.refreshGridSelection();
    this._gridSyncPaused = false;
    this.setupPaletteDrag();
  },

  bindGridSelectionClick(grid) {
    if (!grid?.el) return;
    if (this._gridClickHandler) {
      grid.el.removeEventListener('click', this._gridClickHandler);
    }
    this._gridClickHandler = (event) => {
      const itemEl = event.target?.closest?.('.grid-stack-item');
      if (!itemEl || !grid.el.contains(itemEl)) return;
      const id = String(itemEl.gridstackNode?.id || itemEl.getAttribute('gs-id') || '').trim();
      if (id) this.selectWidget(id);
    };
    grid.el.addEventListener('click', this._gridClickHandler);
  },

  unbindGridContextMenu() {
    if (this._gridStack?.el && this._gridContextMenuHandler) {
      this._gridStack.el.removeEventListener('contextmenu', this._gridContextMenuHandler);
    }
    this._gridContextMenuHandler = null;
    if (this._dismissContextMenuHandler) {
      document.removeEventListener('click', this._dismissContextMenuHandler, true);
      document.removeEventListener('contextmenu', this._dismissContextMenuHandler, true);
      document.removeEventListener('keydown', this._dismissContextMenuHandler);
    }
    this._dismissContextMenuHandler = null;
    this.closeGridContextMenu();
  },

  bindGridContextMenu(grid) {
    if (!grid?.el) return;
    this.unbindGridContextMenu();
    this._gridContextMenuHandler = (event) => {
      const itemEl = event.target?.closest?.('.grid-stack-item');
      if (!itemEl || !grid.el.contains(itemEl)) return;
      event.preventDefault();
      event.stopPropagation();
      const id = String(itemEl.gridstackNode?.id || itemEl.getAttribute('gs-id') || '').trim();
      if (!id) return;
      this.openGridContextMenu(id, event.clientX, event.clientY);
    };
    grid.el.addEventListener('contextmenu', this._gridContextMenuHandler);
    this._dismissContextMenuHandler = (event) => {
      if (!this.gridContextMenu?.open) return;
      if (event.type === 'keydown' && event.key !== 'Escape') return;
      const menu = this.$refs?.gridContextMenuEl;
      if (menu?.contains?.(event.target)) return;
      this.closeGridContextMenu();
    };
    document.addEventListener('click', this._dismissContextMenuHandler, true);
    document.addEventListener('contextmenu', this._dismissContextMenuHandler, true);
    document.addEventListener('keydown', this._dismissContextMenuHandler);
  },

  teardownPaletteDrag() {
    if (this._paletteDrag) {
      try {
        this._paletteDrag.teardown();
      } catch {
        /* ignore */
      }
      this._paletteDrag = null;
    }
  },

  setupPaletteDrag() {
    this.$nextTick(() => {
      const root = this.$root || document;
      this.paintAllPaletteItems(root);

      this.teardownPaletteDrag();
      if (this.canvasPreviewMode || !this._gridStack) return;

      this._paletteDrag = mountBuilderPaletteDrag({
        grid: this._gridStack,
        root,
        trackOverlayEl: this.$refs?.trackOverlayEl || null,
        resolveDef: (paletteKey) => getScorerWidgetPaletteDef(paletteKey),
        onDrop: (payload) => this.dropPaletteWidget(payload),
      });
    });
  },

  dropPaletteWidget({ def, x, y, clientX, clientY, autoPosition = false }) {
    if (this.canvasPreviewMode || !this._gridStack || !def) return;
    const w = Math.max(1, def.defaultColSpan || 1);
    const h = Math.max(1, def.defaultRowSpan || 1);
    let gx = Math.max(0, Number(x) || 0);
    let gy = Math.max(0, Number(y) || 0);
    if (autoPosition) {
      const open = findFirstOpenCell(this._gridStack, w, h);
      if (!open) {
        this.error = 'No room on the grid for this widget.';
        return;
      }
      gx = open.x;
      gy = open.y;
    } else if (Number.isFinite(x) && Number.isFinite(y)) {
      const slot = findNearestDropSlot(this._gridStack, gx, gy, w, h);
      if (!slot) {
        this.error = 'No room on the grid for this widget.';
        return;
      }
      gx = slot.x;
      gy = slot.y;
    } else if (Number.isFinite(clientX) && Number.isFinite(clientY)) {
      const preferred = gridClientPixelToDropSlot(this._gridStack, clientX, clientY, w, h);
      const slot = findNearestDropSlot(this._gridStack, preferred.x, preferred.y, w, h);
      if (!slot) {
        this.error = 'No room on the grid for this widget.';
        return;
      }
      gx = slot.x;
      gy = slot.y;
    } else {
      const slot = findNearestDropSlot(this._gridStack, gx, gy, w, h);
      if (!slot) {
        this.error = 'No room on the grid for this widget.';
        return;
      }
      gx = slot.x;
      gy = slot.y;
    }
    const widget = createWidgetFromCatalog(def.type, {
      id: makeWidgetId(def.type),
      col: gx + 1,
      row: gy + 1,
      colSpan: w,
      rowSpan: h,
      config: { ...(def.defaultConfig || {}) },
    });
    if (!widget) return;
    widget.paletteKey = def.paletteKey;
    const node = widgetToGridStackNode(widget, def);
    node.x = gx;
    node.y = gy;
    node.autoPosition = false;
    this._gridSyncPaused = true;
    const el = this._gridStack.addWidget(node);
    if (el && typeof this._gridStack.update === 'function') {
      this._gridStack.update(el, { x: gx, y: gy, w, h });
    }
    if (el) this.paintWidgetElement(el, widget);
    this._gridSyncPaused = false;
    this.syncFromGrid();
    this.selectedWidgetId = widget.id;
    this.error = '';
  },

  bindGridEvents(grid) {
    const sync = () => {
      if (!this._gridSyncPaused) this.syncFromGrid();
    };
    grid.on('change', sync);
    grid.on('resizestop', sync);
    grid.on('removed', (event, items) => {
      const removedIds = new Set(
        (items || []).map((item) => String(item?.id || item?.el?.getAttribute?.('gs-id') || '')).filter(Boolean),
      );
      if (removedIds.has(String(this.selectedWidgetId || ''))) {
        this.selectedWidgetId = null;
      }
      sync();
    });
    grid.on('added', (event, items) => {
      this.onGridWidgetsAdded(items || []);
      sync();
    });
    grid.on('dragstart', () => {
      this.clearTrackOverlayHighlight();
    });
    grid.on('dragstop', () => {
      this.clearTrackOverlayHighlight();
      sync();
    });
  },

  onGridWidgetsAdded(items) {
    items.forEach((item) => {
      const el = item.el;
      if (!el) return;
      const existingId = String(item.id || el.getAttribute('gs-id') || '').trim();
      const metaWidget = item[GRIDSTACK_WIDGET_META_KEY]
        || item?.gridstackNode?.[GRIDSTACK_WIDGET_META_KEY]
        || el?.gridstackNode?.[GRIDSTACK_WIDGET_META_KEY];
      if (metaWidget?.id && metaWidget?.type) {
        const { col, row } = gridStackToSchemaXY(item.x, item.y);
        const merged = {
          ...metaWidget,
          col,
          row,
          colSpan: item.w || metaWidget.colSpan || 1,
          rowSpan: item.h || metaWidget.rowSpan || 1,
        };
        this.paintWidgetElement(el, merged);
        if (existingId === merged.id) this.selectedWidgetId = merged.id;
        return;
      }
      const existing = (this.activeProfileData().widgets || []).find((w) => w.id === existingId);
      if (existing) {
        this.paintWidgetElement(el, existing);
        return;
      }
      const paletteKey = readPaletteKeyFromElement(el)
        || readPaletteKeyFromNode(item?.gridstackNode || item)
        || readPaletteKeyFromNode(el?.gridstackNode);
      const def = paletteKey ? getScorerWidgetPaletteDef(paletteKey) : null;
      if (!def) return;
      const { col, row } = gridStackToSchemaXY(item.x, item.y);
      const widget = createWidgetFromCatalog(def.type, {
        id: makeWidgetId(def.type),
        col,
        row,
        colSpan: item.w || def.defaultColSpan || 1,
        rowSpan: item.h || def.defaultRowSpan || 1,
        config: { ...(def.defaultConfig || {}) },
      });
      if (!widget) return;
      widget.paletteKey = def.paletteKey;
      if (item.gridstackNode) item.gridstackNode[GRIDSTACK_WIDGET_META_KEY] = widget;
      if (item.gridstackNode) item.gridstackNode.id = widget.id;
      el.setAttribute('gs-id', widget.id);
      el.dataset.widgetId = widget.id;
      this.paintWidgetElement(el, widget);
      this.selectedWidgetId = widget.id;
    });
  },

  applySurfaceFillToChip(chip, widget) {
    if (!chip || !widget) return;
    chip.classList.toggle('scorer-layout-builder-widget-chip--button', widgetHasButtonChrome(widget?.type));
    if (widgetHasSurfaceFill(widget)) {
      chip.classList.add(widgetSurfaceClass(widget));
      const vars = widgetSurfaceCssVars(widget);
      Object.entries(vars).forEach(([key, value]) => {
        chip.style.setProperty(key, value);
      });
      chip.style.setProperty('border-color', 'rgba(255, 255, 255, 0.14)', 'important');
      chip.style.setProperty('background', 'transparent', 'important');
    }
  },

  buildReadoutChipPreview(widget) {
    const specs = {
      'visit.total': { defaultLabel: 'Visit total', sample: 'Tap digits', empty: true },
      'score.input': { defaultLabel: 'Score input', sample: 'Tap digits', empty: true },
      'segment.draft': { defaultLabel: 'Segment draft', sample: 'Tap 3 darts', empty: true, segment: true },
    };
    const spec = specs[String(widget?.type || '')];
    if (!spec) return null;
    const label = widgetConfigDisplayLabel(widget) || spec.defaultLabel;
    const chip = document.createElement('div');
    chip.className = [
      'scorer-layout-builder-widget-chip',
      'scorer-layout-builder-widget',
      'scorer-layout-builder-widget-chip--chrome',
      'scorer-layout-builder-widget-chip--readout',
    ].join(' ');
    chip.dataset.widgetId = widget.id;
    chip.innerHTML = `
      <div class="scorer-layout-builder-readout-preview${spec.segment ? ' scorer-layout-builder-readout-preview--segment' : ''}" aria-hidden="true">
        <span class="scorer-layout-builder-readout-preview__label">${this.escapeHtml(label)}</span>
        <span class="scorer-layout-builder-readout-preview__value${spec.empty ? ' is-empty' : ''}">${this.escapeHtml(spec.sample)}</span>
      </div>
      <span class="scorer-layout-builder-widget-chip__label">${this.escapeHtml(widgetDisplayLabel(widget))}</span>
      <span class="scorer-layout-builder-widget-chip__meta">${widget.colSpan || 1}×${widget.rowSpan || 1}</span>
    `;
    this.applySurfaceFillToChip(chip, widget);
    refreshIcons(chip);
    return chip;
  },

  buildWidgetChipElement(widget) {
    const def = catalogItemForWidget(widget);
    const readoutChip = this.buildReadoutChipPreview(widget);
    if (readoutChip) return readoutChip;
    const chip = document.createElement('div');
    chip.className = [
      'scorer-layout-builder-widget-chip',
      'scorer-layout-builder-widget',
      'scorer-layout-builder-widget-chip--chrome',
      widgetHasButtonChrome(widget?.type) ? 'scorer-layout-builder-widget-chip--button' : '',
    ].filter(Boolean).join(' ');
    if (widget?.type === 'keypad.digitGrid') {
      chip.classList.add('scorer-layout-builder-widget-chip--digit-grid');
      const showQuick = widgetShowQuickScores(widget);
      const quickPreviewCells = showQuick
        ? Array.from({ length: 6 }, () => (
          '<span class="scorer-layout-builder-digit-grid-preview__cell scorer-layout-builder-digit-grid-preview__cell--quick">·</span>'
        )).join('')
        : '';
      const previewCells = [
        quickPreviewCells,
        ...SCORER_DIGIT_GRID_TOP_DIGITS.map(
          (digit) => `<span class="scorer-layout-builder-digit-grid-preview__cell">${digit}</span>`,
        ),
        '<span class="scorer-layout-builder-digit-grid-preview__cell scorer-layout-builder-digit-grid-preview__cell--zero">0</span>',
      ].join('');
      chip.dataset.widgetId = widget.id;
      chip.innerHTML = `
        <div class="scorer-layout-builder-digit-grid-preview${showQuick ? ' scorer-layout-builder-digit-grid-preview--with-quick' : ''}" aria-hidden="true">${previewCells}</div>
        <span class="scorer-layout-builder-widget-chip__label">${this.escapeHtml(widgetDisplayLabel(widget))}</span>
        <span class="scorer-layout-builder-widget-chip__meta">${widget.colSpan || 1}×${widget.rowSpan || 1}</span>
      `;
      this.applySurfaceFillToChip(chip, widget);
      refreshIcons(chip);
      return chip;
    }
    this.applySurfaceFillToChip(chip, widget);
    chip.dataset.widgetId = widget.id;
    const chipIconClass = widgetHasButtonIconOptions(widget?.type)
      ? resolveWidgetButtonIconClass(widget)
      : catalogWidgetIcon(def || widget);
    const chipLabel = widgetButtonFaceText(widget) || widgetDisplayLabel(widget);
    chip.innerHTML = `
      <i class="${chipIconClass} scorer-layout-builder-widget-chip__icon" aria-hidden="true"></i>
      <span class="scorer-layout-builder-widget-chip__label">${this.escapeHtml(chipLabel)}</span>
      <span class="scorer-layout-builder-widget-chip__meta">${widget.colSpan || 1}×${widget.rowSpan || 1}</span>
    `;
    refreshIcons(chip);
    return chip;
  },

  paintWidgetElement(el, widget) {
    if (!el) return;
    const content = el.querySelector('.grid-stack-item-content') || el;
    content.innerHTML = '';
    content.appendChild(this.buildWidgetChipElement(widget));
    content.classList.toggle(
      'scorer-layout-builder-grid-item-content--button',
      widgetHasButtonChrome(widget?.type),
    );
    el.classList.toggle('scorer-layout-builder-grid-item--selected', widget.id === this.selectedWidgetId);
    syncGridWidgetMeta(el.gridstackNode || el, widget);
    refreshIcons(content);
    if (this._gridStack) refreshBuilderGridDrag(this._gridStack, el);
  },

  escapeHtml(value) {
    return String(value || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  },

  syncFromGrid() {
    if (!this._gridStack) return;
    const prof = this.designProfileData();
    prof.widgets = syncWidgetsFromGrid(this._gridStack, prof.widgets || []);
    this.layout.profiles[SCORER_LAYOUT_DESIGN_PROFILE] = prof;
    syncLayoutProfilesFromDesign(this.layout);
    this.refreshGridSelection();
  },

  refreshGridSelection() {
    if (!this._gridStack) return;
    this._gridStack.engine.nodes.forEach((node) => {
      if (!node.el) return;
      node.el.classList.toggle(
        'scorer-layout-builder-grid-item--selected',
        String(node.id || '') === String(this.selectedWidgetId || ''),
      );
    });
  },

  selectedWidget() {
    return (this.activeProfileData().widgets || []).find((w) => w.id === this.selectedWidgetId) || null;
  },

  selectWidget(id) {
    this.selectedWidgetId = id;
    this.refreshGridSelection();
  },

  selectedWidgetIconClass() {
    const w = this.selectedWidget();
    if (!w) return 'fa-solid fa-puzzle-piece';
    return catalogWidgetIcon(catalogItemForWidget(w));
  },

  selectedWidgetSizeLabel() {
    const w = this.selectedWidget();
    if (!w) return '';
    const limits = widgetSizeLimits(w);
    const size = `${w.colSpan || 1}×${w.rowSpan || 1}`;
    if (
      (w.colSpan || 1) >= limits.minColSpan
      && (w.rowSpan || 1) >= limits.minRowSpan
    ) {
      return `${size} · min ${limits.minColSpan}×${limits.minRowSpan}`;
    }
    return size;
  },

  selectedWidgetMinColSpan() {
    const w = this.selectedWidget();
    return w ? widgetSizeLimits(w).minColSpan : 1;
  },

  selectedWidgetMinRowSpan() {
    const w = this.selectedWidget();
    return w ? widgetSizeLimits(w).minRowSpan : 1;
  },

  applyPreset(presetId) {
    const prof = this.designProfileData();
    this.layout.profiles[SCORER_LAYOUT_DESIGN_PROFILE] = applyGridPreset(
      prof,
      presetId,
      SCORER_LAYOUT_DESIGN_PROFILE,
    );
    syncLayoutProfilesFromDesign(this.layout);
    this.selectedWidgetId = null;
    this.$nextTick(() => this.reinitGridStack());
  },

  loadStarterWidgets() {
    const presetId = this.activePresetId() || this.gridPresetsForProfile()[0]?.id;
    const templates = getStarterWidgetTemplates(presetId);
    if (!templates?.length) {
      this.error = 'No starter widgets for this grid preset.';
      return;
    }
    const existing = (this.activeProfileData().widgets || []).length;
    if (existing > 0) {
      const ok = globalThis.confirm('Replace current widgets with the starter set for this preset?');
      if (!ok) return;
    }
    const prof = this.activeProfileData();
    prof.widgets = templates
      .map((tpl) =>
        createWidgetFromCatalog(tpl.type, {
          id: makeWidgetId(tpl.type),
          col: tpl.col,
          row: tpl.row,
          colSpan: tpl.colSpan,
          rowSpan: tpl.rowSpan,
        }),
      )
      .filter(Boolean);
    this.layout.profiles[SCORER_LAYOUT_DESIGN_PROFILE] = prof;
    syncLayoutProfilesFromDesign(this.layout);
    this.selectedWidgetId = null;
    this.error = '';
    this.okMsg = 'Starter widgets loaded.';
    this.$nextTick(() => this.reinitGridStack());
  },

  selectedWidgetHasButton() {
    const w = this.selectedWidget();
    return w ? widgetHasButtonChrome(w.type) : false;
  },

  selectedWidgetHasPlayerSlot() {
    const w = this.selectedWidget();
    return w ? widgetHasPlayerSlot(w.type) : false;
  },

  selectedWidgetHasHeaderOptions() {
    const w = this.selectedWidget();
    return w ? widgetHasHeaderDisplayOptions(w.type) : false;
  },

  selectedWidgetHasScoreboardOptions() {
    const w = this.selectedWidget();
    return w ? widgetHasScoreboardDisplayOptions(w.type) : false;
  },

  selectedWidgetHasDigitGridOptions() {
    const w = this.selectedWidget();
    return w ? widgetHasDigitGridOptions(w.type) : false;
  },

  selectedWidgetSurfaceFill() {
    const w = this.selectedWidget();
    return w ? resolveWidgetSurfaceFillId(w) : 'none';
  },

  selectedWidgetSurfaceOpacity() {
    const w = this.selectedWidget();
    return w ? resolveWidgetSurfaceOpacityPercent(w) : 100;
  },

  surfaceFillPreviewStyle(fill) {
    if (!fill || fill.id === 'none') {
      return {
        background: 'transparent',
        border: '1px dashed rgba(255,255,255,0.25)',
      };
    }
    return {
      background: fill.preview || fill.background,
      border: '1px solid rgba(255,255,255,0.18)',
    };
  },

  selectedWidgetPlayerSlot() {
    const w = this.selectedWidget();
    return w ? normalizePlayerSlot(w.config?.playerSlot) : 'player1';
  },

  playerSlotOptions() {
    return SCORER_PLAYER_SLOT_OPTIONS;
  },

  playerCardRemainingSizeOptions() {
    return SCORER_PLAYER_CARD_REMAINING_SIZE_OPTIONS;
  },

  selectedWidgetRemainingSize() {
    const w = this.selectedWidget();
    return w ? widgetPlayerCardRemainingSize(w) : 'auto';
  },

  selectedWidgetActionLabel() {
    const w = this.selectedWidget();
    return w ? widgetActionLabel(w) : '';
  },

  selectedWidgetDescription() {
    const w = this.selectedWidget();
    if (!w) return '';
    return catalogWidgetDescription(catalogItemForWidget(w)) || '';
  },

  selectedWidgetIsLiveReadout() {
    const type = String(this.selectedWidget()?.type || '');
    return type === 'visit.total' || type === 'score.input' || type === 'segment.draft';
  },

  selectedWidgetReadoutHint() {
    const hints = {
      'visit.total': 'Live total for the visit being typed. Updates as digits are entered; submit with Enter.',
      'score.input': 'Raw digit entry field for the active visit. Pair with a digit keypad and Enter button.',
      'segment.draft': 'Segment games (Cricket, etc.) — shows dart picks before confirming, e.g. T20 · 19 · 25.',
    };
    return hints[String(this.selectedWidget()?.type || '')] || '';
  },

  selectedWidgetActionId() {
    const w = this.selectedWidget();
    return w ? resolveWidgetActionId(w) : '';
  },

  actionPresetsForSelected() {
    const w = this.selectedWidget();
    if (!w) return [];
    return listActionPresetsForWidgetType(w.type);
  },

  actionPresetGroupsForSelected() {
    const w = this.selectedWidget();
    if (!w) return [];
    return listActionPresetsGrouped(w.type);
  },

  updateSelectedAction(actionId) {
    const w = this.selectedWidget();
    if (!w || !this.selectedWidgetHasButton()) return;
    const prof = this.activeProfileData();
    const idx = (prof.widgets || []).findIndex((row) => row.id === w.id);
    if (idx < 0) return;
    prof.widgets[idx] = applyActionPresetToWidget(w, actionId);
    this.syncWidgetVisual(w.id);
    this.error = '';
  },

  selectedWidgetDisplayLabel() {
    const w = this.selectedWidget();
    return w ? widgetDisplayLabel(w) : '';
  },

  selectedWidgetLabelPlaceholder() {
    const w = this.selectedWidget();
    if (!w) return 'Optional label';
    const def = getScorerWidgetDef(w.type);
    const base = def?.label || 'Label';
    if (widgetHasButtonIconOptions(w.type)) return `${base} — leave blank for icon-only`;
    return `${base} — optional override`;
  },

  selectedWidgetHasButtonIconOptions() {
    const w = this.selectedWidget();
    return w ? widgetHasButtonIconOptions(w.type) : false;
  },

  buttonIconOptions() {
    return SCORER_WIDGET_BUTTON_ICON_OPTIONS;
  },

  selectedWidgetButtonIconId() {
    const w = this.selectedWidget();
    return w ? resolveWidgetButtonIconId(w) : '';
  },

  updateSelectedButtonIcon(buttonIcon) {
    const w = this.selectedWidget();
    if (!w || !widgetHasButtonIconOptions(w.type)) return;
    const prof = this.activeProfileData();
    const idx = (prof.widgets || []).findIndex((row) => row.id === w.id);
    if (idx < 0) return;
    const iconId = String(buttonIcon || '').trim();
    prof.widgets[idx] = {
      ...w,
      config: {
        ...(w.config || {}),
        buttonIcon: iconId || widgetDefaultButtonIconId(w.type),
      },
    };
    this.syncWidgetVisual(w.id);
    this.error = '';
  },

  updateSelectedDisplayLabel(value) {
    const w = this.selectedWidget();
    if (!w) return;
    const prof = this.activeProfileData();
    const idx = (prof.widgets || []).findIndex((row) => row.id === w.id);
    if (idx < 0) return;
    prof.widgets[idx] = {
      ...w,
      config: {
        ...(w.config || {}),
        displayLabel: String(value || '').trim(),
      },
    };
    this.syncWidgetVisual(w.id);
  },

  updateSelectedPlayerSlot(playerSlot) {
    const w = this.selectedWidget();
    if (!w || !this.selectedWidgetHasPlayerSlot()) return;
    const prof = this.activeProfileData();
    const idx = (prof.widgets || []).findIndex((row) => row.id === w.id);
    if (idx < 0) return;
    prof.widgets[idx] = {
      ...w,
      config: {
        ...(w.config || {}),
        playerSlot: normalizePlayerSlot(playerSlot),
      },
    };
    this.syncWidgetVisual(w.id);
  },

  selectedWidgetShowAvatar() {
    const w = this.selectedWidget();
    return w ? widgetShowAvatar(w) : true;
  },

  selectedWidgetShowStats() {
    const w = this.selectedWidget();
    return w ? widgetShowStats(w) : true;
  },

  selectedWidgetShowLegs() {
    const w = this.selectedWidget();
    return w ? widgetShowLegs(w) : true;
  },

  selectedWidgetShowRemaining() {
    const w = this.selectedWidget();
    return w ? widgetShowRemaining(w) : true;
  },

  selectedWidgetShowThrowBadge() {
    const w = this.selectedWidget();
    return w ? widgetShowThrowBadge(w) : true;
  },

  selectedWidgetShowNextThrower() {
    const w = this.selectedWidget();
    return w ? widgetShowNextThrower(w) : true;
  },

  selectedWidgetShowLastScore() {
    const w = this.selectedWidget();
    return w ? widgetShowLastScore(w) : true;
  },

  selectedWidgetShowQuickScores() {
    const w = this.selectedWidget();
    return w ? widgetShowQuickScores(w) : false;
  },

  updateSelectedPlayerCardOption(key, value) {
    const w = this.selectedWidget();
    if (!w || (
      !this.selectedWidgetHasPlayerSlot()
      && !this.selectedWidgetHasHeaderOptions()
      && !this.selectedWidgetHasScoreboardOptions()
    )) return;
    const field = String(key || '').trim();
    if (!field) return;
    const prof = this.activeProfileData();
    const idx = (prof.widgets || []).findIndex((row) => row.id === w.id);
    if (idx < 0) return;
    prof.widgets[idx] = {
      ...w,
      config: {
        ...(w.config || {}),
        [field]: Boolean(value),
      },
    };
    this.syncWidgetVisual(w.id);
    this.error = '';
  },

  updateSelectedDigitGridOption(key, value) {
    const w = this.selectedWidget();
    if (!w || !this.selectedWidgetHasDigitGridOptions()) return;
    const field = String(key || '').trim();
    if (!field) return;
    const prof = this.activeProfileData();
    const idx = (prof.widgets || []).findIndex((row) => row.id === w.id);
    if (idx < 0) return;
    const nextConfig = {
      ...(w.config || {}),
      [field]: Boolean(value),
    };
    let nextRowSpan = w.rowSpan || 1;
    if (field === 'showQuickScores') {
      const limits = widgetSizeLimits({ ...w, config: nextConfig });
      if (Boolean(value)) {
        nextRowSpan = Math.max(nextRowSpan, limits.minRowSpan, SCORER_DIGIT_GRID_WITH_QUICK_ROW_COUNT);
      }
    }
    prof.widgets[idx] = {
      ...w,
      rowSpan: nextRowSpan,
      config: nextConfig,
    };
    this.syncWidgetVisual(w.id);
    this.error = '';
  },

  updateSelectedPlayerCardRemainingSize(remainingSize) {
    const w = this.selectedWidget();
    if (!w || !this.selectedWidgetHasPlayerSlot()) return;
    const prof = this.activeProfileData();
    const idx = (prof.widgets || []).findIndex((row) => row.id === w.id);
    if (idx < 0) return;
    prof.widgets[idx] = {
      ...w,
      config: {
        ...(w.config || {}),
        remainingSize: normalizePlayerCardRemainingSize(remainingSize),
      },
    };
    this.syncWidgetVisual(w.id);
    this.error = '';
  },

  updateSelectedSurfaceFill(surfaceFill) {
    const w = this.selectedWidget();
    if (!w) return;
    const prof = this.activeProfileData();
    const idx = (prof.widgets || []).findIndex((row) => row.id === w.id);
    if (idx < 0) return;
    const fillId = widgetSurfaceFillDef(String(surfaceFill || 'none')).id;
    prof.widgets[idx] = {
      ...w,
      config: {
        ...(w.config || {}),
        surfaceFill: fillId,
      },
    };
    this.syncWidgetVisual(w.id);
  },

  updateSelectedSurfaceOpacity(value) {
    const w = this.selectedWidget();
    if (!w) return;
    const prof = this.activeProfileData();
    const idx = (prof.widgets || []).findIndex((row) => row.id === w.id);
    if (idx < 0) return;
    prof.widgets[idx] = {
      ...w,
      config: {
        ...(w.config || {}),
        surfaceOpacity: resolveWidgetSurfaceOpacityPercent({ config: { surfaceOpacity: value } }),
      },
    };
    this.syncWidgetVisual(w.id);
  },

  syncWidgetVisual(widgetId) {
    const w = (this.activeProfileData().widgets || []).find((row) => row.id === widgetId);
    const node = this._gridStack?.engine?.nodes?.find((n) => String(n.id) === String(widgetId));
    if (!w || !node?.el) return;
    this.paintWidgetElement(node.el, w);
  },

  widgetTitle(widget) {
    return widgetLabel(widget);
  },

  widgetCatalogIcon(item) {
    return catalogWidgetIcon(item);
  },

  widgetCatalogDescription(item) {
    return catalogWidgetDescription(item);
  },

  widgetCatalogSizeLabel(item) {
    return catalogWidgetSizeLabel(item);
  },

  gridWidgetElement(widgetId) {
    const node = this._gridStack?.engine?.nodes?.find((n) => String(n.id) === String(widgetId));
    return node?.el || null;
  },

  removeWidgetById(id) {
    const widgetId = String(id || '').trim();
    if (!widgetId || !this._gridStack) return;
    const el = this.gridWidgetElement(widgetId);
    if (el) this._gridStack.removeWidget(el, true);
    else {
      const prof = this.activeProfileData();
      prof.widgets = (prof.widgets || []).filter((w) => w.id !== widgetId);
    }
    if (String(this.selectedWidgetId || '') === widgetId) {
      this.selectedWidgetId = null;
    }
    this.syncFromGrid();
    this.closeGridContextMenu();
  },

  removeSelectedWidget() {
    this.removeWidgetById(this.selectedWidgetId);
  },

  updateSelectedSpan(field, value) {
    const w = this.selectedWidget();
    if (!w || !this._gridStack) return;
    const def = getScorerWidgetDef(w.type);
    const n = Math.max(1, Number(value) || 1);
    const patch = {};
    if (field === 'colSpan') patch.w = Math.max(def?.minColSpan || 1, n);
    if (field === 'rowSpan') patch.h = Math.max(def?.minRowSpan || 1, n);
    const el = this.gridWidgetElement(w.id);
    if (el) this._gridStack.update(el, patch);
    this.syncFromGrid();
    this.error = '';
  },

  stageInlinePreviewCatalog() {
    this.syncFromGrid();
    const slug = String(this.layout?.id || '').trim() || 'builder-preview';
    try {
      scorerLayoutCatalogState.bySlug.set(slug, JSON.parse(JSON.stringify(this.layout)));
    } catch {
      scorerLayoutCatalogState.bySlug.set(slug, this.layout);
    }
    this.customLayoutPreviewSlug = slug;
    return true;
  },

  enterCanvasPreview() {
    if (!this.stageInlinePreviewCatalog()) return;
    this.closeGridContextMenu();
    this.scorerLayoutTheme = toLayoutThemeId(this.customLayoutPreviewSlug);
    this.match = createPreviewMockMatch();
    this.fixture = { homeTeamName: 'Home', awayTeamName: 'Away' };
    this.segmentDraft = 'T20 · T19 · T17';
    this.segmentDarts = ['T20', 'T19', 'T17'];
    this.editingHistoryIndex = null;
    this.scoreInput = '';
    this.previewToastVisible = false;
    this.canvasPreviewMode = true;
    this.selectedWidgetId = null;
    this.teardownPaletteDrag();
    this.destroyGridStack();
    this.error = '';
    this.measureCanvasViewport();
    this.$nextTick(() => {
      this.syncInlinePreviewDeviceContext?.();
      this.paintInlinePreviewTrackOverlay(this.$refs?.inlinePreviewHost);
    });
  },

  exitCanvasPreview() {
    if (!this.canvasPreviewMode) return;
    this.canvasPreviewMode = false;
    this.previewToastVisible = false;
    this.match = null;
    this.fixture = null;
    this.segmentDraft = '';
    this.segmentDarts = [];
    this.editingHistoryIndex = null;
    this.scorerLayoutTheme = '';
    this.customLayoutPreviewSlug = '';
    this.scoreInput = '';
    this.$nextTick(() => this.reinitGridStack());
  },

  openPreviewDialog() {
    this.enterCanvasPreview();
  },

  closePreviewDialog() {
    this.exitCanvasPreview();
  },

  async saveDraft() {
    this.syncFromGrid();
    syncLayoutProfilesFromDesign(this.layout);
    const id = normalizeLayoutSlug(this.layout?.id);
    if (!id) {
      this.error = 'Layout id is required (slug format).';
      return;
    }
    const validation = validateLayout({ ...this.layout, id }, { forPublish: false });
    if (!validation.ok) {
      this.error = formatValidationFeedback(validation);
      this.okMsg = '';
      return;
    }
    this.saving = true;
    this.error = '';
    this.okMsg = '';
    try {
      const body = { ...this.layout, id, published: false };
      const res = await apiFetch(`/api/scorer-layouts/${encodeURIComponent(id)}`, {
        method: 'PUT',
        body,
      });
      const data = await parseApiJson(res);
      if (!res.ok) {
        if (data?.validation) {
          this.error = formatValidationFeedback(data.validation);
        } else {
          this.error = data?.message || data?.error || 'Save failed';
        }
        return;
      }
      this.layout = cloneLayout(data.layout || body);
      await this.loadList({ preferId: id });
      this.okMsg = 'Draft saved.';
    } catch (e) {
      this.error = e?.message || 'Save failed';
    } finally {
      this.saving = false;
    }
  },

  async publishLayout() {
    this.syncFromGrid();
    syncLayoutProfilesFromDesign(this.layout);
    const id = normalizeLayoutSlug(this.layout?.id);
    if (!id) {
      this.error = 'Layout id is required (slug format).';
      return;
    }
    const validation = validateLayout({ ...this.layout, id }, { forPublish: true });
    if (!validation.ok) {
      this.error = formatValidationFeedback(validation);
      this.okMsg = '';
      return;
    }
    this.publishing = true;
    this.error = '';
    this.okMsg = '';
    try {
      const body = { ...this.layout, id, published: false };
      const saveRes = await apiFetch(`/api/scorer-layouts/${encodeURIComponent(id)}`, {
        method: 'PUT',
        body,
      });
      const saveData = await parseApiJson(saveRes);
      if (!saveRes.ok) {
        if (saveData?.validation) {
          this.error = formatValidationFeedback(saveData.validation);
        } else {
          this.error = saveData?.message || saveData?.error || 'Save failed';
        }
        return;
      }
      this.layout = cloneLayout(saveData.layout || body);
      const res = await apiFetch(`/api/scorer-layouts/${encodeURIComponent(id)}/publish`, {
        method: 'POST',
      });
      const data = await parseApiJson(res);
      if (!res.ok) {
        if (data?.validation) {
          this.error = formatValidationFeedback(data.validation);
        } else {
          this.error = data?.message || data?.error || 'Publish failed';
        }
        return;
      }
      this.layout = cloneLayout(data.layout || this.layout);
      await this.loadList({ preferId: id });
      this.okMsg = 'Layout published to all kiosks.';
    } catch (e) {
      this.error = e?.message || 'Publish failed';
    } finally {
      this.publishing = false;
    }
  },

  async unpublishLayout() {
    const id = normalizeLayoutSlug(this.layout?.id);
    if (!id) return;
    this.publishing = true;
    this.error = '';
    this.okMsg = '';
    try {
      const res = await apiFetch(`/api/scorer-layouts/${encodeURIComponent(id)}/unpublish`, {
        method: 'POST',
      });
      const data = await parseApiJson(res);
      if (!res.ok) {
        this.error = data?.message || data?.error || 'Unpublish failed';
        return;
      }
      this.layout = cloneLayout(data.layout || this.layout);
      await this.loadList({ preferId: id });
      this.okMsg = 'Layout unpublished.';
    } catch (e) {
      this.error = e?.message || 'Unpublish failed';
    } finally {
      this.publishing = false;
    }
  },
}));
